import Splide from '@splidejs/splide';
import { unitlessBreakpoints } from '../helpers/breakpoints';

const selectors = {
  carousel: '[data-video-carousel]',
  card: '[data-video-card]',
  playButton: '[data-video-play-button]',
  pauseButton: '[data-video-pause-button]',
};

const classes = {
  dimmed: 'opacity-70',
};

export default class DynamicVideoCarousel {
  constructor(section) {
    this.el = section.el;
    this.data = section.data || {};
    this.carouselEl = this.el.querySelector(selectors.carousel);
    this.cards = Array.from(this.el.querySelectorAll(selectors.card));
    this.splide = null;
    this.activeCard = null;

    this._init();
  }

  _init() {
    if (!this.carouselEl || !this.data.slideCount) {
      return;
    }

    this.el.addEventListener('click', this.onClick);

    // `ended` does not bubble, so the dim state has to be cleared from capture.
    this.el.addEventListener('ended', this.onEnded, true);

    this.splide = new Splide(this.carouselEl, this._buildOptions());
    this.splide.on('move', this._reset);
    this.splide.mount();
  }

  /**
   * Mobile is the base layout, so `min` media queries widen it: a single
   * centred slide peeking both neighbours, then a plain multi-up track once
   * there is room for it.
   */
  _buildOptions() {
    const { perPageMobile, perPageTablet, perPageDesktop, peek, arrows } =
      this.data;

    return {
      perMove: 1,
      arrows,
      pagination: false,
      mediaQuery: 'min',
      perPage: perPageMobile,
      focus: 'center',
      padding: { left: peek, right: peek },
      gap: '1rem',
      breakpoints: {
        [unitlessBreakpoints.md]: {
          perPage: perPageTablet,
          focus: 0,
          padding: 0,
        },
        [unitlessBreakpoints.lg]: {
          perPage: perPageDesktop,
          focus: 0,
          padding: 0,
        },
      },
    };
  }

  /**
   * Video instances are owned by initVideos() and never exposed, but their
   * whole state lives on the DOM — so playback is driven by clicking the
   * buttons Video.js already binds rather than by reaching for the instance.
   */
  onClick = event => {
    const play = event.target.closest(selectors.playButton);

    if (play) {
      this._setActive(play.closest(selectors.card));
      return;
    }

    const pause = event.target.closest(selectors.pauseButton);

    // Pausing the other cards below re-enters this handler. Only the card the
    // user actually paused should clear the state that was just set.
    if (pause && pause.closest(selectors.card) === this.activeCard) {
      this._reset();
    }
  };

  onEnded = event => {
    if (event.target.closest(selectors.card) === this.activeCard) {
      this._reset();
    }
  };

  _setActive = card => {
    if (!card) {
      return;
    }

    this.activeCard = card;

    this.cards.forEach(other => {
      if (other === card) {
        other.classList.remove(classes.dimmed);
        return;
      }

      other.querySelector(selectors.pauseButton)?.click();

      if (this.data.dimInactive) {
        other.classList.add(classes.dimmed);
      }
    });
  };

  _reset = () => {
    const card = this.activeCard;

    if (!card) {
      return;
    }

    // Cleared first: the synthetic click below re-enters onClick, and the
    // guard there only holds once this is no longer the active card.
    this.activeCard = null;
    card.querySelector(selectors.pauseButton)?.click();
    this.cards.forEach(other => other.classList.remove(classes.dimmed));
  };

  onSectionUnload() {
    this.el.removeEventListener('click', this.onClick);
    this.el.removeEventListener('ended', this.onEnded, true);

    if (this.splide) {
      this.splide.destroy();
      this.splide = null;
    }
  }
}
